<script setup lang="ts">
import { computed, onBeforeMount, ref, watch } from "vue";
import { useI18n } from "vue-i18n";
import {
	emptyHierarchySelection,
	HierarchyBranch,
	HierarchySelection,
	RuleDimension,
} from "@/modules/rule_engine/ruleEngineClasses";
import { useRuleEnginedStore } from "@/modules/rule_engine/store";
import HierarchyTree from "@/modules/explorer/components/FilterDialog/HierarchyTree.vue";
import { useHierarchyTree } from "@/modules/explorer/components/FilterDialog/useHierarchyTree";
import { branchOfLabelAndLevel } from "@/modules/explorer/components/FilterDialog/hierarchyTree";
import HierarchyDefinitionDO = App.Data.Hierarchy.HierarchyDefinitionDO;
import QuickFilterNodeDTO = App.Data.Explorer.Filter.QuickFilterNodeDTO;
import FilterValueDTO = App.Data.Explorer.Filter.FilterValueDTO;

const { t } = useI18n();
const store = useRuleEnginedStore();

const props = defineProps<{
	type: "in_hierarchy" | "not_in_hierarchy";
	selectedRuleDimension: RuleDimension;
}>();

const selection = defineModel<HierarchySelection>();
const isInputValid = defineModel<boolean>("isInputValid", {
	default: () => ref(true),
});

const definitions = ref<HierarchyDefinitionDO[]>([]);
const isLoadingDefinitions = ref(false);

/*
 * The explorer's own hierarchy picker drives the selection, so a condition is picked from exactly
 * the tree — nodes and assets — the backend matches it against.
 */
const root = ref<QuickFilterNodeDTO | null>(null);
const tree = useHierarchyTree(root);
const loadedDefinitionId = ref<number | null>(null);
const isLoadingTree = ref(false);
const hasLoadFailed = ref(false);

/** Saved branches the loaded tree no longer has: renamed, moved or deleted since. */
const missingBranches = ref<HierarchyBranch[]>([]);

/** The tree's state right after the saved selection was applied, to tell user edits apart. */
let loadedSnapshot = "";

const selectedDefinitionId = computed({
	get: () => selection.value?.hierarchy_definition_id ?? null,
	// Labels belong to one tree, so a selection is meaningless against a different one.
	set: (definitionId) => {
		selection.value = emptyHierarchySelection(definitionId);
	},
});

const isTreeReady = computed(
	() =>
		loadedDefinitionId.value !== null &&
		loadedDefinitionId.value === selectedDefinitionId.value
);

const asFilterValue = (value: HierarchySelection): FilterValueDTO => ({
	id: null,
	model: null,
	label: value.scope?.label ?? null,
	level: value.scope?.level ?? null,
	selection_depth: value.selection_depth,
	sort_order: null,
	excluded_branches: value.excluded_branches,
});

const findMissingBranches = (value: HierarchySelection): HierarchyBranch[] =>
	[...(value.scope ? [value.scope] : []), ...value.excluded_branches].filter(
		(branch) =>
			branchOfLabelAndLevel(root.value, branch.level, branch.label) === null
	);

onBeforeMount(async () => {
	isLoadingDefinitions.value = true;
	definitions.value = await store.getHierarchyDefinitions(
		props.selectedRuleDimension.uuid
	);
	isLoadingDefinitions.value = false;

	if (selectedDefinitionId.value === null) {
		const preferred =
			definitions.value.find((definition) => definition.is_default) ??
			definitions.value[0];

		if (preferred) {
			selectedDefinitionId.value = preferred.id;
		}
	}
});

watch(
	selectedDefinitionId,
	async (definitionId) => {
		loadedDefinitionId.value = null;
		hasLoadFailed.value = false;
		missingBranches.value = [];

		if (definitionId === null) return;

		isLoadingTree.value = true;
		const loaded = await store.getHierarchyTree(
			props.selectedRuleDimension.uuid,
			definitionId
		);
		isLoadingTree.value = false;

		// The user already moved on to another tree.
		if (definitionId !== selectedDefinitionId.value) return;

		if (!loaded) {
			hasLoadFailed.value = true;
			return;
		}

		const saved = selection.value ?? emptyHierarchySelection(definitionId);

		root.value = loaded;
		tree.loadFromValue(asFilterValue(saved));
		missingBranches.value = findMissingBranches(saved);
		loadedSnapshot = JSON.stringify(tree.toFilterValue());
		loadedDefinitionId.value = definitionId;
	},
	{ immediate: true }
);

/*
 * Only edits are written back. Opening a condition must not rewrite it, least of all when the tree
 * fell back to its root because a saved branch is gone — that would silently widen the rule.
 */
watch(
	() => tree.toFilterValue(),
	(value) => {
		if (!isTreeReady.value || JSON.stringify(value) === loadedSnapshot) return;

		missingBranches.value = [];
		selection.value = {
			hierarchy_definition_id: selectedDefinitionId.value,
			scope:
				value.level === null || value.label === null
					? null
					: { level: value.level, label: value.label },
			selection_depth: value.selection_depth ?? 1,
			excluded_branches: value.excluded_branches ?? [],
		};
	},
	{ deep: true }
);

watch(
	isTreeReady,
	(ready) => {
		isInputValid.value = ready;
	},
	{ immediate: true }
);
</script>

<template>
	<div class="d-flex">
		<div class="pr-5 py-4 label-column">{{ t("rule_engine.hierarchy.dimension") }}</div>
		<div class="py-4">
			<strong>{{
				t(
					`rule_engine.rule_dimensions.${props.selectedRuleDimension.name_clean}.title`
				)
			}}</strong>
		</div>
	</div>

	<div class="d-flex">
		<div class="pr-5 pt-2 label-column">{{ t("rule_engine.hierarchy.tree") }}</div>
		<div class="select-container">
			<v-select
				v-model="selectedDefinitionId"
				:items="definitions"
				item-title="name"
				item-value="id"
				:loading="isLoadingDefinitions"
				:placeholder="t('rule_engine.hierarchy.select_tree')"
				hide-details="auto"
			/>
		</div>
	</div>

	<div class="d-flex mt-4">
		<div class="pr-5 pt-2 label-column">{{ t("rule_engine.hierarchy.select") }}</div>
		<div class="tree-container">
			<v-alert
				v-if="missingBranches.length > 0"
				type="warning"
				density="compact"
				class="mb-2"
			>
				{{ t("rule_engine.hierarchy.missing_branches_warning") }}
				<strong>{{ missingBranches.map((branch) => branch.label).join(", ") }}</strong>
			</v-alert>

			<div class="tree-area">
				<v-alert v-if="hasLoadFailed" type="error" density="compact">
					{{ t("rule_engine.hierarchy.load_failed") }}
				</v-alert>

				<div v-else-if="isLoadingTree" class="pa-4 text-center">
					<v-progress-circular indeterminate size="24" />
				</div>

				<hierarchy-tree v-else-if="isTreeReady" :tree="tree" />
			</div>

			<div class="mt-2 text-caption text-secondary">
				{{ t("rule_engine.hierarchy.selection_hint") }}
			</div>
		</div>
	</div>
</template>

<style scoped lang="scss">
.label-column {
	width: 250px;
	min-width: 250px;
}

.select-container {
	width: 100%;
	max-width: 500px;
}

.tree-container {
	width: 100%;
	max-width: 640px;
}

/*
 * Reserves room for a typical level of the tree, so drilling into a smaller level or reloading the
 * tree does not shift the rest of the condition. Larger levels still grow up to FilterList's own cap.
 */
.tree-area {
	min-height: 18rem;
}
</style>
